import { captureException } from '../../../../../util/captureException'
import { DatePipe } from '@angular/common'
import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTableModule } from '@angular/material/table'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { finalize, from, switchMap, tap } from 'rxjs'
import { InboundApiKeyStatus } from '../../../../../../_generated/graphql'
import { ButtonComponent } from '../../../../../components/button/button.component'
import { DateInputComponent } from '../../../../../components/date-input/date-input.component'
import { LoadingComponent } from '../../../../../components/loading/loading.component'
import {
  AmazonConnectOperationError,
  ExternalIntegrationService,
} from '../../../../../services/external-integration/external-integration.service'
import { InboundApiKey } from '../../../../../services/external-integration/external-integration.mapping'

// backend/src/datasources/amazonConnectService.ts の同名定数と揃える。
const MAX_ACTIVE_INBOUND_API_KEYS = 2

export type InboundApiKeyManagementData = { intakeActive: boolean }

@UntilDestroy()
@Component({
  selector: 'app-inbound-api-key-management',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatIcon,
    MatTableModule,
    ButtonComponent,
    DateInputComponent,
    LoadingComponent,
  ],
  templateUrl: './inbound-api-key-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./inbound-api-key-management.component.scss'],
})
export class InboundApiKeyManagementComponent implements OnInit {
  readonly maxActiveInboundApiKeys = MAX_ACTIVE_INBOUND_API_KEYS
  keys: InboundApiKey[] = []
  readonly columns = [
    'label',
    'keyId',
    'last4',
    'status',
    'created',
    'lastUsedAt',
    'expiresAt',
    'actions',
  ]
  readonly statusLabels: Record<InboundApiKeyStatus, string> = {
    [InboundApiKeyStatus.Active]: '有効',
    [InboundApiKeyStatus.Revoked]: '失効',
    [InboundApiKeyStatus.Expired]: '期限切れ',
  }
  readonly activeStatus = InboundApiKeyStatus.Active
  readonly expiredStatus = InboundApiKeyStatus.Expired
  label = new FormControl('', {
    nonNullable: true,
    validators: [Validators.maxLength(100)],
  })
  expiresAt = new FormControl<Date | null>(null)
  revokeReason = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(200)],
  })
  revokingKey: InboundApiKey | null = null
  issuedKey: string | null = null
  isCloseConfirmationVisible = false
  isLoading = false
  hasLoaded = false
  errorMessage: string | null = null

  /** 発行結果が描画されたら画面内へ移動してフォーカスする。 */
  @ViewChild('issuedKeyResult')
  set issuedKeyResult(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) this.focusElement(ref.nativeElement)
  }

  /** 失効理由フォームが描画されたら入力へ移動する。 */
  @ViewChild('revokeReasonInput')
  set revokeReasonInput(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) this.focusElement(ref.nativeElement)
  }

  /** スクロール非対応のテスト環境でもフォーカスを維持する。 */
  private focusElement(el: HTMLElement): void {
    if (typeof el.scrollIntoView === 'function')
      el.scrollIntoView({ block: 'start' })
    el.focus()
  }

  /** キー情報をコンポーネント内だけに保持する。 */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InboundApiKeyManagementData,
    private externalIntegrationService: ExternalIntegrationService,
    private dialogRef: MatDialogRef<InboundApiKeyManagementComponent>,
    private snackBar: MatSnackBar,
  ) {}

  /** 最新のキー一覧を取得する。 */
  ngOnInit(): void {
    this.loadKeys()
  }

  /** 通信中と上限到達時は発行を抑止する。 */
  get isIssueDisabled(): boolean {
    return (
      this.isLoading ||
      !this.hasLoaded ||
      this.issuedKey != null ||
      this.label.invalid ||
      this.expiresAt.invalid ||
      this.activeKeyCount >= MAX_ACTIVE_INBOUND_API_KEYS
    )
  }

  /** API が返した有効状態のキー数を数える。 */
  get activeKeyCount(): number {
    return this.keys.filter((key) => key.status === InboundApiKeyStatus.Active)
      .length
  }

  /** 一覧取得失敗は画面に表示し、古い件数による発行を抑止する。 */
  loadKeys(): void {
    this.isCloseConfirmationVisible = false
    this.isLoading = true
    this.errorMessage = null
    this.externalIntegrationService
      .getInboundApiKeys()
      .pipe(
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe(this.keysObserver())
  }

  /** 一覧更新と失敗時の発行抑止を全操作で共通化する。 */
  private keysObserver(message?: () => string | null) {
    return {
      next: (keys: InboundApiKey[]) => {
        this.keys = keys
        this.hasLoaded = true
      },
      error: (error: unknown) => {
        captureException(error)
        this.hasLoaded = false
        const operationMessage = message?.()
        this.errorMessage =
          operationMessage == null
            ? this.toErrorMessage(error)
            : operationMessage
      },
    }
  }

  /** キーを発行して一度だけ平文を表示し、メタデータの一覧を再取得する。 */
  issueKey(): void {
    if (this.isIssueDisabled) return
    const expiry = this.expiresAt.value
    if (
      expiry != null &&
      (!Number.isFinite(expiry.getTime()) ||
        expiry.getTime() < Date.now() + 86400000)
    ) {
      this.errorMessage =
        '有効期限は発行から 1 日以上先の日付を指定してください'
      return
    }
    this.isLoading = true
    this.errorMessage = null
    this.issuedKey = null
    const label = this.label.value.trim()
    this.externalIntegrationService
      .issueInboundApiKey({
        label: label === '' ? null : label,
        expiresAt: expiry,
      })
      .pipe(
        tap((result) => {
          this.issuedKey = result.key
          this.isCloseConfirmationVisible = false
          this.label.reset()
          this.expiresAt.reset()
        }),
        switchMap(() => this.externalIntegrationService.getInboundApiKeys()),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe(
        this.keysObserver(() =>
          this.issuedKey != null
            ? 'API キーは発行されました。一覧の再取得に失敗したため「一覧を再取得」を押してください。'
            : null,
        ),
      )
  }

  /** 失効理由入力を対象キーに紐づける。 */
  startRevoke(key: InboundApiKey): void {
    if (this.isLoading || key.status !== InboundApiKeyStatus.Active) return
    this.isCloseConfirmationVisible = false
    this.revokingKey = key
    this.revokeReason.reset()
    this.errorMessage = null
  }

  /** 失効入力を取り消す。 */
  cancelRevoke(): void {
    this.isCloseConfirmationVisible = false
    this.revokingKey = null
    this.revokeReason.reset()
  }

  /** 理由を必須として失効し、最新一覧を再取得する。 */
  revokeKey(): void {
    const key = this.revokingKey
    const reason = this.revokeReason.value.trim()
    if (
      this.isLoading ||
      key == null ||
      reason === '' ||
      this.revokeReason.invalid
    )
      return
    let revoked = false
    this.isLoading = true
    this.errorMessage = null
    this.externalIntegrationService
      .revokeInboundApiKey({ keyId: key.keyId, reason })
      .pipe(
        tap(() => {
          revoked = true
          this.cancelRevoke()
        }),
        switchMap(() => this.externalIntegrationService.getInboundApiKeys()),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe(
        this.keysObserver(() =>
          revoked
            ? '失効は完了しました。一覧の再取得に失敗したため「一覧を再取得」を押してください。'
            : null,
        ),
      )
  }

  /** 表示中のキーだけを利用者の操作でクリップボードへコピーする。 */
  copyKey(): void {
    if (this.issuedKey == null) return
    if (!navigator.clipboard) {
      this.errorMessage =
        'コピーできません。表示されたキーを選択してコピーしてください。'
      return
    }
    from(navigator.clipboard.writeText(this.issuedKey))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.snackBar.open('API キーをコピーしました。', '閉じる')
        },
        error: (error: unknown) => {
          captureException(error)
          this.errorMessage =
            'コピーに失敗しました。表示されたキーを選択してコピーしてください。'
        },
      })
  }

  /** 平文表示中は保存確認を挟み、親画面にキーを返さず閉じる。 */
  close(): void {
    if (this.isLoading) return
    if (this.issuedKey != null && !this.isCloseConfirmationVisible) {
      this.isCloseConfirmationVisible = true
      return
    }
    this.issuedKey = null
    this.dialogRef.close()
  }

  /** 上限超過を専用表示し、サーバーの業務エラーメッセージを表示する。 */
  private toErrorMessage(error: unknown): string {
    return error instanceof AmazonConnectOperationError
      ? error.message
      : 'API キーの操作に失敗しました。時間をおいて再度お試しください。'
  }
}
